import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store/ducks';
import store from 'store';

import Text from '../../Text';
import { Row, Column } from '../../Layout/Grid';
import { Button } from '../../Button';
import RadioInput from 'components/RadioInput';
import toast from 'components/Toast/dispatch';
import SwitchComponent from 'components/SwitchComponent';

import IconSuccess from 'img/success-img.svg';

import {
  PhoneNumberOutput,
  PhoneNumberTest,
  PhoneNumberParse,
} from 'utils/PhoneNumber';
import UserService from 'services/user.service';
import { DeleteCustomerRoleUserProps, IUser } from 'models/User';
import { ICustomerRole } from 'models/CustomerRole';

const ButtonRemove = (onClickFunc: Function, loading: boolean, text: string) => {

  return (
    <SwitchComponent roles={['administrator']} type={['employee']}>
      <Row className="mt-3 align-center justify-center">
        <Button
          onClick={() => onClickFunc()}
          spinnerColorClass="color-danger"
          isRequesting={loading}
          className="outline border-danger bg-danger flex-sm-1 align-center justify-center d-flex px-3"
        >
          <Text className="color-danger font-bold">{text}</Text>
        </Button>
      </Row>
    </SwitchComponent>
  );
};

const EditUser: React.FC = () => {
  const slider = useSelector((state: RootState) => state.slider);
  const novo = slider === undefined;
  const dispatch = useDispatch();
  const { customer } = store.getState();
  const { user } = store.getState();

  function status(data: IUser) {
    let value = false;
    if (user?.userType === 'employee') {
      value =
        (data?.enabled &&
          data?.customersRoles?.find((c) => c.customerId === customer?.id)
            ?.enabled) ||
        false;
    } else {
      value =
        (data?.enabled &&
          data?.customersRoles?.find(
            (c: ICustomerRole) => user?.selectedCustomerId?.includes(c.customerId!)
          )?.enabled) ||
        false;
    }
    return value;
  }

  function pending(data: IUser) {
    return data.pending;
  }

  const [userStatus, setUserStatus] = useState<string>('');
  const [newUserData, setNewUserData] = useState({
    id: slider?.state?.id,
    fullName: slider?.state?.fullName,
    email: slider?.state?.email,
    phoneNumber: PhoneNumberOutput(slider?.state?.phoneNumber || ''),
    roles: slider?.state?.roles,
    profileType: slider?.state?.profileType,
    whoInvited: slider?.state?.whoInvited,
    lastAccess: slider?.state?.lastAccess,
    expirationDate: slider?.state?.expirationDate,
    userType: slider?.state?.userType,
    enabled: status(slider?.state as IUser),
    pending: pending(slider?.state as IUser),
    username: slider?.state?.userName,
    customerId: customer?.id ?? user?.selectedCustomerId,
  });

  const _userService = new UserService();

  const [loading, setLoading] = useState(false);
  const [loadingRemoveLinkCompany, setLoadingRemoveLinkCompany] = useState(false);
  const [loadingDeleteUser, setLoadingDeleteUser] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [typeRemoveButton, setTypeRemoveButton] = useState('editado');

  async function editUser() {
    setErrorMessage('');

    const data = {
      id: newUserData.id,
      fullName: newUserData.fullName,
      email: newUserData.email,
      username: newUserData.email,
      phoneNumber: newUserData.phoneNumber,
      roles: newUserData.roles,
      profileType: newUserData.profileType,
      enabled: newUserData.enabled,
      pending: newUserData.pending,
      customerId: newUserData.customerId,
    };

    if (!data.id) return setErrorMessage('Esse usuário parece não ter um id');
    if (!data.fullName) return setErrorMessage('Campo nome é requerido');
    if (data.fullName.length < 5)
      return setErrorMessage(
        'Campo nome precisa conter pelo menos 5 caracteres'
      );
    if (!data.email) return setErrorMessage('Campo e-mail é requerido');
    if (!data.phoneNumber) return setErrorMessage('Campo telefone é requerido');
    if (!PhoneNumberTest(data.phoneNumber))
      return setErrorMessage('Digite um telefone válido');

    data.phoneNumber = PhoneNumberParse(data.phoneNumber);
    setLoading(true);

    await _userService
      .update(data)
      .then(() => {
        dispatch({
          type: 'HIDE_TITLE',
        });
        setSuccess(true);
      })
      .catch((err) => {
        setLoading(false);
        return setErrorMessage(
          err.response.data.data || 'Falha ao realizar requisição'
        );
      });
  }

  function onCloseSlider() {
    if (slider?.state?.currentPage > 0) {
      slider?.state?.setCurrentPage(0);
    } else {
      slider?.state?.getUsers();
    }
    dispatch({ type: 'CLOSE_SLIDER' });
    dispatch({ type: 'SHOW_TITLE' });
  }

  useEffect(() => {
    if (newUserData.pending) {
      setUserStatus('pending');
    } else if (!newUserData.pending && newUserData.enabled) {
      setUserStatus('enabled');
    } else if (!newUserData.enabled) {
      setUserStatus('disabled');
    }
  }, [newUserData]);


  async function confirmDeleteCustomerRole() {
    toast.yesNoProtocol(
      'Atenção',
      `Você deseja realmente remover o vinculo com a empresa ${customer?.companyName}?`,
      () => deleteCustomerRole(),
      'Sim',
      'Não');
  }

  async function confirmDeleteUser() {
    toast.yesNoProtocol(
      'Atenção',
      `Você deseja realmente excluir o acesso do usuário ${newUserData.fullName ?? newUserData.email} ao Customer Care?`,
      () => deleteUser(),
      'Sim',
      'Não');
  }

  async function deleteCustomerRole() {
    setLoadingRemoveLinkCompany(true);
    setErrorMessage('');

    const userDeleted: DeleteCustomerRoleUserProps = {
      userId: newUserData?.id,
      customerId: customer?.id
    };

    const response = await _userService.deleteCustomerRoleUser(userDeleted);

    if (response.error) {
      setSuccess(false);
      setErrorMessage('Um erro inesperado ocorreu.');
    } else {
      dispatch({
        type: 'HIDE_TITLE',
      });
      setSuccess(true);
      setTypeRemoveButton("editado");
    }

    setLoadingRemoveLinkCompany(false);
    dispatch({ type: 'CLOSE_TOAST' });
  }

  async function deleteUser() {
    setLoadingDeleteUser(true);
    setErrorMessage('');

    const userDeleted: DeleteCustomerRoleUserProps = {
      userId: newUserData?.id
    };

    const response = await _userService.deleteCustomer(userDeleted);

    if (response.error) {
      setSuccess(false);
      setErrorMessage('Um erro inesperado ocorreu.');
    } else {
      dispatch({
        type: 'HIDE_TITLE',
      });
      setSuccess(true);
      setTypeRemoveButton("excluído");
    }

    setLoadingDeleteUser(false);
    dispatch({ type: 'CLOSE_TOAST' });
  }

  return !success ? (
    <>
      <Column className="mt-3 flex-1">
        <Column>
          <Text className="color-blue-gray mb-2">Status</Text>
          <Row>
            <Row className="mr-2">
              <RadioInput
                id="radio_enabled"
                onClick={() =>
                  setNewUserData({ ...newUserData, enabled: true, pending: false })
                }
                checked={userStatus === 'enabled'}
              >
                <Text>Ativo</Text>
              </RadioInput>
            </Row>

            <Row>
              <RadioInput
                id="radio_disabled"
                onClick={() =>
                  setNewUserData({ ...newUserData, enabled: false, pending: false })
                }
                checked={userStatus === 'disabled'}
              >
                <Text>Inativo</Text>
              </RadioInput>
            </Row>
          </Row>
        </Column>

        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Nome</Text>
          <input
            className="w-sm-100"
            value={newUserData.fullName}
            onChange={(e) =>
              setNewUserData({ ...newUserData, fullName: e.target.value })
            }
            type="text"
            placeholder="Nome"
          />
        </Column>

        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Email</Text>
          <input
            className="w-sm-100"
            value={newUserData.email}
            type="text"
            placeholder="E-mail"
            disabled
          />
        </Column>

        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Telefone</Text>
          <Row>
            <input
              className="w-sm-100"
              value={newUserData.phoneNumber}
              onChange={(e) =>
                setNewUserData({
                  ...newUserData,
                  phoneNumber: PhoneNumberOutput(e.target.value),
                })
              }
              type="text"
              placeholder="Telefone"
            />
          </Row>
        </Column>

        <Column className="mt-3">
          <Text className="color-blue-gray mb-2">Nível de acesso</Text>
          <Row>
            <Row className="mr-2">
              <RadioInput
                id="radio_administrator"
                onClick={() =>
                  setNewUserData({ ...newUserData, roles: ['administrator'] })
                }
                checked={newUserData.roles?.[0] === 'administrator'}
              >
                <Text>Administrador</Text>
              </RadioInput>
            </Row>

            <Row>
              <RadioInput
                id="radio_collaborator"
                onClick={() =>
                  setNewUserData({ ...newUserData, roles: ['member'] })
                }
                checked={newUserData.roles?.[0] === 'member'}
              >
                <Text>Colaborador</Text>
              </RadioInput>
            </Row>
          </Row>
        </Column>
        {errorMessage && (
          <Text className="color-danger mt-2 animated fadeIn" size={14}>
            {errorMessage}
          </Text>
        )}
        <Row className="mt-3 align-center justify-center">
          <Button
            spinnerColor="#0E4C54"
            isRequesting={loading}
            disabled={loading}
            onClick={editUser}
            className="d-flex outline bg-primary flex-sm-1 align-center justify-center px-3"
          >
            <Text className="color-primary font-bold">Salvar</Text>
          </Button>
        </Row>

        {ButtonRemove(confirmDeleteCustomerRole, loadingRemoveLinkCompany, "Remover vínculo com a empresa")}
        {ButtonRemove(confirmDeleteUser, loadingDeleteUser, "Excluir usuário do Customer Care")}
      </Column>
    </>
  ) : (
    <Column className="w-100 h-100 align-center justify-center">
      <img src={IconSuccess} alt="" />
      <Text className="mt-2 color-blue-gray font-bold">
        {`Usuário ${typeRemoveButton} com sucesso`}
      </Text>
      {novo &&
        <Text className="mt-3 color-blue-gray" size={12}>
          O usuário receberá por e-mail um link para completar seu cadastro
        </Text>
      }
      <Button onClick={onCloseSlider} className="outline mt-4" width="auto">
        <Text className="color-primary font-bold px-3">Fechar</Text>
      </Button>
    </Column>
  );
};

export default EditUser;
